import React from 'react';
import styled from 'styled-components';
import { Field, Label, Control } from 'rbx';
import InputErrorMessage from './InputErrorMessage';

const OptionalText = styled.p`
  &&& {
    color: #737373;
    padding-left: 2px;
  }
`;

const TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  padding: 4px 8px;
  min-height: 38px;
  background: white;
  &:focus-within {
    border-color: #3273dc;
    box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
  }
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #e8e8e8;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.85em;
  color: #363636;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  color: #666;
  font-size: 14px;
  &:hover {
    color: #cc0000;
  }
`;

const PlainInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  min-width: 120px;
  font-size: 1rem;
  padding: 2px 0;
  background: transparent;
`;

/**
 * MultiTextInput — free-form multi-value text input.
 *
 * Props:
 *   value     — string[]         current list of values
 *   onChange  — (string[]) => void   called with the updated array
 *   label, placeholder, required, disabled, optionalText, errors, touched
 *
 * UX:
 *   - Press Enter, Tab, or comma to add the current text as a chip
 *   - Press Backspace on an empty input to remove the last chip
 *   - Click × on a chip to remove it
 *   - Duplicate values are ignored
 *   - Blurring with text in the input auto-adds it
 */
const MultiTextInput = ({
  label,
  value = [],
  onChange,
  placeholder = 'Type and press Enter',
  required,
  disabled,
  errors,
  touched,
  optionalText,
}) => {
  const [inputValue, setInputValue] = React.useState('');

  const addValue = val => {
    const trimmed = val.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInputValue('');
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
      e.preventDefault();
      addValue(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  const removeValue = index => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <Field>
      {label && (
        <Label className="is-flex">
          {label}
          {required && <span className="has-text-danger">*</span>}
          {optionalText && <OptionalText>{optionalText}</OptionalText>}
        </Label>
      )}
      <Control>
        <TagsWrapper>
          {value.map((v, i) => (
            <Tag key={i}>
              {v}
              {!disabled && (
                <RemoveBtn type="button" onClick={() => removeValue(i)}>
                  ×
                </RemoveBtn>
              )}
            </Tag>
          ))}
          {!disabled && (
            <PlainInput
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => inputValue.trim() && addValue(inputValue)}
              placeholder={value.length === 0 ? placeholder : ''}
            />
          )}
        </TagsWrapper>
      </Control>
      <InputErrorMessage errors={errors} touched={touched} />
    </Field>
  );
};

export default MultiTextInput;
